import dayjs from "dayjs";

export interface ErrorLog {
  /** 追踪ID，用于链路追踪 */
  traceId: string;

  /** 错误发生时间（前端通常用字符串格式，如 '2026-01-27 10:00:00'） */
  errorTime: string;

  /** 错误请求的URL */
  errorUrl: string;

  /** 错误详细信息（堆栈/描述） */
  errorMessage: string;
}
// 使用 IndexedDB 存储错误
const DB_NAME = "rantion-error-db";
const DB_VERSION = 1;
const STORE_NAME = "error-logs";

/**
 * 初始化 IndexedDB 数据库
 */
function initDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(new Error("Failed to open database"));
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "id", autoIncrement: true });
        store.createIndex("traceId", "traceId", { unique: true });
        store.createIndex("errorTime", "errorTime", { unique: false });
        store.createIndex("errorUrl", "errorUrl", { unique: false });
      }
    };
  });
}

/**
 * 保存错误日志到 IndexedDB
 */
export async function saveHttpError(errorData: Rantion.AjaxData<any>): Promise<void> {
  if (!errorData.traceId) {
    return;
  }
  try {
    const db = await initDB();
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);

    const errorLog: ErrorLog & { id?: number } = {
      traceId: errorData.traceId || "",
      errorTime: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      errorUrl: window.location.href,
      errorMessage: errorData.msg || "",
    };

    await new Promise<void>((resolve, reject) => {
      const request = store.add(errorLog);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error("Failed to save error log"));
    });

    db.close();
  } catch (error) {
    console.error("Error saving to IndexedDB:", error);
  }
}

/**
 * 获取所有错误日志
 */
export async function getAllErrorLogs(): Promise<(ErrorLog & { id: number })[]> {
  try {
    const db = await initDB();
    const transaction = db.transaction(STORE_NAME, "readonly");
    const store = transaction.objectStore(STORE_NAME);

    const logs = await new Promise<(ErrorLog & { id: number })[]>((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(new Error("Failed to get error logs"));
    });

    db.close();
    return logs;
  } catch (error) {
    console.error("Error getting error logs:", error);
    return [];
  }
}

/**
 * 根据 traceId 获取错误日志
 */
export async function getErrorLogsByTraceId(
  traceId: string,
): Promise<(ErrorLog & { id: number })[]> {
  try {
    const db = await initDB();
    const transaction = db.transaction(STORE_NAME, "readonly");
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index("traceId");

    const logs = await new Promise<(ErrorLog & { id: number })[]>((resolve, reject) => {
      const request = index.getAll(traceId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(new Error("Failed to get error logs by traceId"));
    });

    db.close();
    return logs;
  } catch (error) {
    console.error("Error getting error logs by traceId:", error);
    return [];
  }
}

/**
 * 清空所有错误日志
 */
export async function clearErrorLogs(): Promise<void> {
  try {
    const db = await initDB();
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);

    await new Promise<void>((resolve, reject) => {
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error("Failed to clear error logs"));
    });

    db.close();
  } catch (error) {
    console.error("Error clearing error logs:", error);
  }
}

/**
 * 清理超过指定天数的错误日志
 * @param days 天数阈值
 */
export async function clearErrorLogsOlderThan(days: number): Promise<void> {
  try {
    const db = await initDB();
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);

    // 获取所有错误日志
    const logs = await new Promise<(ErrorLog & { id: number })[]>((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(new Error("Failed to get error logs"));
    });

    // 计算目标日期
    const targetDate = dayjs().subtract(days, "day");

    // 删除超过指定天数的日志
    for (const log of logs) {
      const logDate = dayjs(log.errorTime);
      if (logDate.isBefore(targetDate)) {
        await new Promise<void>((resolve, reject) => {
          const request = store.delete(log.id);
          request.onsuccess = () => resolve();
          request.onerror = () => reject(new Error(`Failed to delete log with id ${log.id}`));
        });
      }
    }

    db.close();
  } catch (error) {
    console.error("Error clearing old error logs:", error);
  }
}
